import { Box } from "@mui/material";
import { useMemo } from "react";
import { FormikProps } from "formik";
import Text from "../../../../../../../../../../components/Text";
import Flex from "../../../../../../../../../../components/Flex";
import MaskMultiSelect from "../../../../../../../../../../components/MaskMultiSelect";
import { ScrollArea } from "../../../../../../../../../../components/ScrollBar";
import DeleteIcon from "../../../../../../../../../../components/app/DeleteIcon";
import StyledFormModal from "../../../../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../../../../hooks/use-disclosure";
import useGetCataLogCode from "../../../../../../../../../../queries/catalog/use-get-catalog-code";
import { Form2Payload } from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import useFormDraft from "@/hooks/use-form-draft";
import {
  DEFAULT_BG_COLOR,
  DEFAULT_FONTSIZE,
  DEF_PAGINATION,
} from "../../../../../../../../../../utils/utils";
/** A code as stored on the note: `{ value, code }`. */
type Code = NonNullable<Form2Payload["assessmentCodes"]>[number];

const ASSESSMENT_CODES_FIELD = "assessmentCodes";

interface AddIcdCodesProps {
  formik: FormikProps<Form2Payload>;
  disclosureHost: DisclosureHost;
  height?: string;
}

// Kept here for the note sections that render the selected list outside this modal.

export const renderSelectedIcdCodes = (
  selectedIcdCodes: Code[],
  handleDeleteIcdCode: (code: Code) => void,
  height?: string
) => {
  if (!selectedIcdCodes || selectedIcdCodes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
      }}
    >
      <Text fontWeight="medium" fontSize="12px" color="#373D41" mb={1}>
        Selected ICD Codes:
      </Text>
      <ScrollArea maxHeight={height ?? "35vh"} height={"100%"}>
        <Flex flexDirection="column" gap={1}>
          {selectedIcdCodes.map((code, index) => {
            // Parse the code to extract ICD code and description
            const codeString =
              typeof code === "string" ? code : code?.code || "";
            const codeParts = codeString.split(" - ");
            const icdCode = codeParts[0];
            const description = codeParts.slice(1).join(" - ");

            return (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: DEFAULT_BG_COLOR,
                  borderRadius: "4px",
                  px: 0.3,
                  gap: "12px",
                }}
              >
                <Flex alignItems="center" gap={0.5}>
                  <Text fontSize={DEFAULT_FONTSIZE} fontWeight="400">
                    {icdCode}
                  </Text>
                  <Text fontSize={DEFAULT_FONTSIZE} fontWeight="400">
                    - {description}
                  </Text>
                </Flex>
                <DeleteIcon onClick={() => handleDeleteIcdCode(code)} />
              </Box>
            );
          })}
        </Flex>
      </ScrollArea>
    </Box>
  );
};

export const AddIcdCodes = ({ formik, disclosureHost }: AddIcdCodesProps) => {
  const [searchIcdKey, setSearchIcdKey] = useDebounce("");
  const { data: icdCodes } = useGetCataLogCode({
    pagination: DEF_PAGINATION,
    searchKey: searchIcdKey,
    type: "ICD",
    active: true,
    archive: false,
    enabled: disclosureHost.opened,
  });
  // The picker stays mounted between openings, so the selection is buffered in a
  // draft that re-snapshots the note every time it opens — a code deleted from
  // the note would otherwise still show as selected in here.
  const draft = useFormDraft(
    formik,
    [ASSESSMENT_CODES_FIELD],
    disclosureHost.opened
  );
  const selectedIcdCodes = useMemo(
    () => draft.values?.assessmentCodes ?? [],
    [draft.values?.assessmentCodes]
  );
  const setSelectedIcdCodes = (codes: Code[]) =>
    draft.setFieldValue(ASSESSMENT_CODES_FIELD, codes);
  const fetchedCodes: Code[] = useMemo(
    () =>
      icdCodes?.data?.content?.map((it) => ({
        value: it?.uuid ?? "",
        code: `${it?.code} - ${it?.description || ""}`,
      })) ?? [],
    [icdCodes]
  );

  // Only the codes returned for the current search are offered. Retaining the
  // pages fetched earlier would keep every previous result in the list and push
  // the freshly matched code to the bottom, where it reads as "not returned".
  // Selected codes are still prepended so the dropdown - which resolves its
  // labels from `options` - can render them when the current page omits them.
  const transFormedIcdCodes = useMemo(
    () => [
      ...selectedIcdCodes.filter(
        (selected) =>
          !fetchedCodes.some((option) => option.value === selected?.value)
      ),
      ...fetchedCodes,
    ],
    [fetchedCodes, selectedIcdCodes]
  );

  const handleDeleteIcdCode = (codeToDelete: Code) =>
    setSelectedIcdCodes(
      selectedIcdCodes.filter((code) => code?.value !== codeToDelete?.value)
    );

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle="Add ICD Codes"
      dialogAction={
        <>
          <StyledButtonV2
            label="Close"
            variant="outlined"
            onClick={() => {
              disclosureHost.close();
            }}
            sx={{ width: "100px" }}
          />
          <StyledButtonV2
            label="Add"
            variant="outlined"
            onClick={() => {
              draft.save();
              disclosureHost.close();
            }}
            sx={{
              width: "110px",
              backgroundColor: "#CCECFF80",
              color: "#004186",
            }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "40vw",
          height: "65vh",
        },
      }}
    >
      <Box
        sx={{
          mt: 1,
          width: "100%",
          pt: 2,
        }}
      >
        <MaskMultiSelect
          placeholder="Search & Select ICD Codes"
          value={selectedIcdCodes}
          onSearch={setSearchIcdKey}
          options={transFormedIcdCodes}
          onChange={(v) => setSelectedIcdCodes(v ?? [])}
        />
        {renderSelectedIcdCodes(selectedIcdCodes, handleDeleteIcdCode, "35vh")}
      </Box>
    </StyledFormModal>
  );
};
